import type { FormConfig, FormMode } from '@cod/config-schema';
import { Box, Button, Card, Tabs, useToast } from '@nimbus-ds/components';
import { Layout, Page } from '@nimbus-ds/patterns';
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { DesignerSkeleton } from '../components/skeletons.js';
import { LivePreview } from '../designer/LivePreview.js';
import { ModeSwitch } from '../designer/ModeSwitch.js';
import { DesignerProblems } from '../designer/ProblemsContext.js';
import { AppearanceTab, ButtonTab, CoverageTab, FieldsTab, TextTab } from '../designer/tabs.js';
import {
  configProblems,
  firstActionable,
  type DesignerTab,
  type ProblemMap,
} from '../designer/validation.js';
import { useAppState } from '../state/AppState.js';
import { useUnsavedChanges } from '../state/NavigationGuard.js';

/** Stable empty map: a fresh one per render would re-run every consumer's `useMemo`. */
const EMPTY_PROBLEMS: ProblemMap = new Map();

export function FormDesigner(): JSX.Element {
  const { t } = useTranslation();
  const { config, saveConfig } = useAppState();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState('button');
  const [saving, setSaving] = useState(false);
  /**
   * Whether a save has been attempted, which is what reveals the inline errors.
   *
   * Marking every empty box red the moment the page opens would scold the merchant for editing
   * they have not finished. Same gate Settings uses for the thank-you problem.
   */
  const [attemptedSave, setAttemptedSave] = useState(false);
  // Edits are staged on a local draft, not the shared saved config, so we can offer Save
  // (persist the draft) and Discard (drop back to the saved baseline). The baseline is the
  // AppState `config`, which only changes reference on a save — so this effect resyncs the
  // draft after a save (clearing dirty) without clobbering in-progress edits on other renders.
  const [draft, setDraft] = useState<FormConfig | null>(config);
  useEffect(() => {
    setDraft(config);
  }, [config]);

  // Deep compare so edits that cancel out (e.g. toggle off then on) clear the Save/Discard state.
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(config), [draft, config]);

  // Everything the schema objects to in the draft, keyed by control path. Recomputed per edit so
  // a message disappears as soon as the merchant fixes the value, without another save attempt.
  const problems = useMemo(() => (draft ? configProblems(draft) : EMPTY_PROBLEMS), [draft]);
  /**
   * Whether the SAVED config is already invalid.
   *
   * 🚨 Not hypothetical: a config can be stored from an older schema, or through another surface,
   * and then every save from this page fails from the first click with nothing to explain it. When
   * that is the situation the merchant is in, the errors show immediately rather than waiting for
   * them to press Save and be refused. Settings does the same for the thank-you handoff.
   */
  const savedBroken = useMemo(() => (config ? configProblems(config).size > 0 : false), [config]);
  const visibleProblems = attemptedSave || savedBroken ? problems : EMPTY_PROBLEMS;

  // Leaving the designer with a staged draft threw the whole form design away without a word.
  // The same Discard the bar below offers is what the guard's prompt runs.
  useUnsavedChanges(dirty, () => setDraft(config));

  // Same updater signature the tabs already expect from AppState.setConfig, but scoped to the draft.
  const update = useCallback((updater: (prev: FormConfig) => FormConfig) => {
    setDraft((prev) => (prev ? updater(prev) : prev));
  }, []);

  // Defensive: the shell already gates on `status`, so this only fires if the config is
  // momentarily absent. The skeleton is the page's own two-column shape, so even this branch
  // cannot reflow the layout when the draft arrives.
  if (!config || !draft) return <DesignerSkeleton />;

  const isPopup = draft.mode === 'popup';

  const setMode = (mode: FormMode): void => update((c) => ({ ...c, mode }));

  const onSave = async (): Promise<void> => {
    setAttemptedSave(true);
    // 🚨 Refuse locally rather than letting the request come back 422. `configProblems` runs the
    // same `formConfig` schema the backend validates with, so this cannot reject something the
    // server would have accepted — and it is the only path on which the offending control gets
    // marked. Sending it anyway would repaint the identical unhelpful toast.
    if (problems.size > 0) {
      const first = firstActionable(
        problems,
        tabs.map((tab) => tab.id as DesignerTab),
      );
      // Jump to the tab that owns the first problem, since it is very often not the one the
      // merchant is looking at. The inline message on the control says what is actually wrong;
      // the toast only has to explain why nothing saved.
      if (first) setActiveTab(first.tab);
      addToast({
        id: 'save-config',
        type: 'danger',
        // No actionable problem means the config is broken somewhere this view cannot reach —
        // another page (shipping rates, thank-you, tracking, offers) or a tab the current mode
        // hides, like Button in embedded mode. Say so instead of pointing at fields that are fine.
        text: first
          ? t('designer.validation.saveBlocked')
          : t('designer.validation.saveBlockedElsewhere'),
      });
      return;
    }
    setSaving(true);
    const ok = await saveConfig(draft);
    setSaving(false);
    if (ok) setAttemptedSave(false);
    addToast({
      id: 'save-config',
      type: ok ? 'success' : 'danger',
      text: ok ? t('common.saved') : t('common.saveError'),
    });
  };

  const onDiscard = (): void => {
    setDraft(config);
    setAttemptedSave(false);
  };

  // Tabs available depend on the mode: button options only exist in popup mode.
  const tabs: { id: string; label: string }[] = [
    ...(isPopup ? [{ id: 'button', label: t('designer.tabs.button') }] : []),
    { id: 'fields', label: t('designer.tabs.fields') },
    { id: 'coverage', label: t('designer.tabs.coverage') },
    { id: 'appearance', label: t('designer.tabs.appearance') },
    { id: 'text', label: t('designer.tabs.text') },
  ];
  const current = tabs.some((tab) => tab.id === activeTab) ? activeTab : tabs[0]!.id;

  // Each column scrolls on its own so a tall preview and a tall config panel don't drag
  // each other. The offset clears the AppShell header + the sticky actions bar that sit above
  // the columns; paddingRight keeps the scrollbar from clipping card shadows / focus rings.
  const columnScroll: CSSProperties = {
    maxHeight: 'calc(100vh - 120px)',
    overflowY: 'auto',
    paddingRight: 8,
    // 🚨 Both of these are what keep the page off a horizontal scrollbar on a phone.
    //
    // `Layout` collapses to one column below `md` (`gridTemplateColumns: {xs:'1fr', md:…}`), but a
    // `1fr` track is `minmax(auto, 1fr)` — its floor is the content's MIN-CONTENT width, and the
    // live preview renders the real form at its real 460px panel width. So the single column
    // measured 528px inside a 390px viewport and pushed the whole page sideways: the mode cards,
    // the tab bar and the preview were all cut off at the right edge, which is what a merchant
    // sees on mobile. `minWidth: 0` lets the track shrink; `overflowX: auto` then gives the one
    // thing that genuinely cannot shrink — the preview — its own scrollbar instead of the page's.
    minWidth: 0,
    overflowX: 'auto',
  };
  // Keeps Save/Discard pinned to the top of the page so they're always reachable while scrolling.
  // The bar matches the page surface token (so it also tracks light/dark) — scrolled content is
  // hidden seamlessly beneath it rather than behind a white box.
  const stickyActions: CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    background: 'var(--nimbus-colors-neutral-surface)',
    paddingTop: 12,
  };

  return (
    <Page maxWidth="1200px">
      <Page.Body>
        {/* Sticky Save/Discard bar (no page title): stays pinned at the top so the actions are
            always reachable while the config and preview columns scroll. The background matches
            the page surface so content scrolls invisibly beneath it. Both are disabled until the
            draft actually differs from the saved config. */}
        <div style={stickyActions}>
          <Box display="flex" justifyContent="flex-end" gap="2" paddingBottom="3">
            <Button onClick={onDiscard} disabled={!dirty || saving}>
              {t('common.discard')}
            </Button>
            <Button appearance="primary" onClick={() => void onSave()} disabled={!dirty || saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </Box>
        </div>
        <Layout columns="2 - symmetric">
          {/* `minWidth="0"` on the SECTION as well as the scroll div: the section is the grid
              item, and a grid item's automatic minimum size is its min-content unless told
              otherwise — so without this the track still cannot shrink below the preview. */}
          <Layout.Section minWidth="0">
            <div style={columnScroll}>
              {/* Mode switch is the first control on the page. */}
              <ModeSwitch mode={draft.mode} onChange={setMode} />

              <Box marginTop="4">
                <Card>
                  <Card.Body>
                    <Tabs
                      selected={Math.max(
                        0,
                        tabs.findIndex((tab) => tab.id === current),
                      )}
                      onTabSelect={(index) => setActiveTab(tabs[index]!.id)}
                    >
                      {tabs.map((tab) => (
                        <Tabs.Item key={tab.id} label={tab.label}>
                          <Box marginTop="4">
                            <DesignerProblems problems={visibleProblems}>
                              {tab.id === 'button' && <ButtonTab config={draft} update={update} />}
                              {tab.id === 'fields' && <FieldsTab config={draft} update={update} />}
                              {tab.id === 'coverage' && (
                                <CoverageTab config={draft} update={update} />
                              )}
                              {tab.id === 'appearance' && (
                                <AppearanceTab config={draft} update={update} />
                              )}
                              {tab.id === 'text' && <TextTab config={draft} update={update} />}
                            </DesignerProblems>
                          </Box>
                        </Tabs.Item>
                      ))}
                    </Tabs>
                  </Card.Body>
                </Card>
              </Box>
            </div>
          </Layout.Section>

          <Layout.Section minWidth="0">
            {/* Independent scroll region: a tall preview scrolls on its own without
                moving the config column. */}
            <div style={columnScroll}>
              <Card>
                <Card.Header title={t('designer.preview')} />
                <Card.Body>
                  <Box backgroundColor="neutral-surface" padding="4" borderRadius="2">
                    <LivePreview config={draft} />
                  </Box>
                </Card.Body>
              </Card>
            </div>
          </Layout.Section>
        </Layout>
      </Page.Body>
    </Page>
  );
}
